import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend, LineChart, Line,
} from "recharts";
import {
  Wallet, Tv, Youtube, Users, Target, Calculator, Layers, PiggyBank,
  ArrowRight, FileSpreadsheet, RefreshCw, AlertTriangle, Database, Loader2, Plus, Shuffle,
} from "lucide-react";
import KpiCard from "@/components/KpiCard";
import CurveChart from "@/components/CurveChart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtCurrency, fmtPercent, fmtNumber, fmtFullCurrency } from "@/lib/format";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

const CHART_COLORS = ["#0A3D91", "#18B7D8", "#1CC7B3", "#0E73C9", "#94A3B8"];

function Skeleton({ className }) {
  return <div className={`bg-slate-100 animate-pulse rounded-lg ${className}`} />;
}

function EmptyKpi({ label, hint }) {
  return (
    <div className="card-harmony p-5 flex flex-col">
      <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-300 mt-3 leading-tight">—</div>
      <div className="text-xs text-slate-400 mt-1">{hint}</div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { campaigns, activeCampaignId, activeCampaign, setActiveCampaignId, refreshCampaigns } = useActiveCampaign();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [curveDetail, setCurveDetail] = useState(null);
  const [curveLoading, setCurveLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  // ---- Replace Curve Set inline action ----
  const [allCurves, setAllCurves] = useState([]);
  const [replaceCurveOpen, setReplaceCurveOpen] = useState(false);
  const [pendingCurveId, setPendingCurveId] = useState("");
  const [replaceBusy, setReplaceBusy] = useState(false);

  const openReplaceCurve = async () => {
    setPendingCurveId(dashboard?.curve_set?.id || "");
    setReplaceCurveOpen(true);
    if (allCurves.length === 0) {
      try {
        const { data } = await api.get("/curve-sets");
        setAllCurves(data);
      } catch {
        // toast handled by axios
      }
    }
  };

  const confirmReplaceCurve = async () => {
    if (!pendingCurveId || !activeCampaignId) return;
    if (pendingCurveId === dashboard?.curve_set?.id) {
      setReplaceCurveOpen(false);
      return;
    }
    setReplaceBusy(true);
    try {
      const c = dashboard?.campaign || {};
      // PUT requires campaign_name; send full campaign with just the curve set swapped.
      await api.put(`/campaigns/${activeCampaignId}`, {
        campaign_name: c.campaign_name,
        client: c.client ?? null,
        agency: c.agency ?? null,
        date_start: c.date_start ?? null,
        date_end: c.date_end ?? null,
        currency: c.currency ?? "IDR",
        target_group: c.target_group ?? null,
        universe_size: c.universe_size ?? null,
        total_budget: c.total_budget ?? null,
        tv_budget: c.tv_budget ?? null,
        olv_budget: c.olv_budget ?? null,
        effective_frequency: c.effective_frequency ?? 1,
        selected_curve_set_id: pendingCurveId,
        notes: c.notes ?? null,
        status: c.status ?? "active",
        vendor_parameter_id: c.vendor_parameter_id ?? null,
        vendor_parameter_name: c.vendor_parameter_name ?? null,
        parameter_source_type: c.parameter_source_type ?? null,
        parameter_snapshot: c.parameter_snapshot ?? null,
      });
      const picked = allCurves.find((x) => x.id === pendingCurveId);
      toast.success(`Curve set replaced with "${picked?.curve_name || "selection"}". Dashboard refreshed.`);
      setReplaceCurveOpen(false);
      await loadDashboard();
      refreshCampaigns();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReplaceBusy(false);
    }
  };

  const loadDashboard = useCallback(async () => {
    if (!activeCampaignId) {
      setDashboard(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard/by-campaign/${activeCampaignId}`);
      setDashboard(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to load dashboard:", err);
      toast.error(formatApiError(err) || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [activeCampaignId]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Fetch full curve detail for the new "Selected Curve" Dashboard section.
  useEffect(() => {
    const curveId = dashboard?.curve_set?.id;
    if (!curveId) {
      setCurveDetail(null);
      return;
    }
    let cancelled = false;
    setCurveLoading(true);
    api.get(`/curve-sets/${curveId}`)
      .then(({ data }) => { if (!cancelled) setCurveDetail(data); })
      .catch(() => { if (!cancelled) setCurveDetail(null); })
      .finally(() => { if (!cancelled) setCurveLoading(false); });
    return () => { cancelled = true; };
  }, [dashboard?.curve_set?.id]);

  // Empty state: no campaigns
  if (campaigns.length === 0) {
    return (
      <div className="card-harmony p-12 text-center">
        <Database className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h2 className="font-display text-xl font-bold text-slate-900">No campaign available</h2>
        <p className="text-sm text-slate-500 mt-2">
          Create a campaign or upload curve files to start.
        </p>
        <div className="flex gap-2 justify-center mt-6">
          <button onClick={() => nav("/campaigns")} className="btn-primary" data-testid="dashboard-empty-create-campaign">
            <Plus className="h-4 w-4" /> Create Campaign
          </button>
          <button onClick={() => nav("/upload-curve")} className="btn-secondary">
            <FileSpreadsheet className="h-4 w-4" /> Upload Curve Files
          </button>
        </div>
      </div>
    );
  }

  // No active campaign selected
  if (!activeCampaignId) {
    return (
      <div className="card-harmony p-12 text-center">
        <Database className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <h2 className="font-display text-xl font-bold text-slate-900">No campaign selected</h2>
        <p className="text-sm text-slate-500 mt-2">Pick a campaign to view its dashboard.</p>
        <Select value={activeCampaignId} onValueChange={(v) => { setActiveCampaignId(v); toast.success("Active campaign changed"); }}>
          <SelectTrigger className="max-w-xs mx-auto mt-6" data-testid="dashboard-campaign-select">
            <SelectValue placeholder="Select campaign…" />
          </SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>{c.campaign_name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const source = dashboard?.source || "manual";
  const warnings = dashboard?.warnings || [];
  const scenario = dashboard?.selected_scenario;
  const camp = dashboard?.campaign || activeCampaign;

  const budgetSplit = [
    { name: "TV", value: kpis.tv_budget || 0, color: "#0A3D91" },
    { name: "OLV", value: kpis.olv_budget || 0, color: "#18B7D8" },
  ];
  const reachContribution = scenario ? [
    { name: "TV", reach: kpis.tv_reach || 0 },
    { name: "OLV", reach: kpis.olv_reach || 0 },
    { name: "Combined Reach", reach: kpis.combined_reach || 0 },
  ] : [];

  const sourceLabel = {
    scenario: "Selected Campaign + Selected Scenario",
    computed: "Selected Campaign + Live Calculation",
    manual: "Selected Campaign (no scenario)",
  }[source];

  const totalBudget = kpis.total_budget || 0;

  return (
    <div className="space-y-6">
      {/* Top control bar */}
      <div className="card-harmony overflow-hidden">
        <div className="harmony-accent-bar h-1.5 w-full" />
        <div className="p-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              Active Campaign
            </div>
            <div className="flex items-center gap-3 mt-1">
              <Select
                value={activeCampaignId}
                onValueChange={(v) => { setActiveCampaignId(v); toast.success("Active campaign changed"); }}
              >
                <SelectTrigger className="min-w-[280px] max-w-md font-display text-base font-semibold" data-testid="dashboard-campaign-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (<SelectItem key={c.id} value={c.id}>{c.campaign_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {camp && (
              <div className="text-sm text-slate-500 mt-2" data-testid="dashboard-campaign-meta">
                {camp.client && <span>{camp.client}</span>}
                {camp.agency && <span> · {camp.agency}</span>}
                {camp.target_group && <span> · Target {camp.target_group}</span>}
                {camp.universe_size && <span> · Universe {fmtNumber(camp.universe_size)}</span>}
              </div>
            )}
            <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
              <span className="inline-flex items-center gap-1">
                <Database className="h-3 w-3" /> Source: {sourceLabel}
              </span>
              {lastUpdated && (
                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={loadDashboard} className="btn-secondary" data-testid="dashboard-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={() => nav(`/optimizer?campaign=${activeCampaignId}`)} className="btn-primary" data-testid="dashboard-go-optimizer">
              Open Optimizer <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-1" data-testid="dashboard-warnings">
          {warnings.map((w) => (
            <div key={w} className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" /> {w}
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[1,2,3,4,5,6,7,8].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <>
          {/* KPI Row 1 - Budget */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <KpiCard
              testId={TID.kpiTotalBudget}
              label="Total Budget"
              value={kpis.total_budget ? `${camp?.currency || "IDR"} ${fmtCurrency(kpis.total_budget)}` : "—"}
              sub={kpis.total_budget ? fmtFullCurrency(kpis.total_budget, camp?.currency) : "Not set"}
              icon={Wallet}
              accent="deep"
            />
            <KpiCard
              testId={TID.kpiTvBudget}
              label="TV Budget"
              value={kpis.tv_budget ? `${camp?.currency || "IDR"} ${fmtCurrency(kpis.tv_budget)}` : "—"}
              sub={kpis.tv_allocation_pct != null ? `${kpis.tv_allocation_pct.toFixed(1)}% allocation` : ""}
              icon={Tv}
              accent="blue"
            />
            <KpiCard
              testId={TID.kpiOlvBudget}
              label="OLV Budget"
              value={kpis.olv_budget ? `${camp?.currency || "IDR"} ${fmtCurrency(kpis.olv_budget)}` : "—"}
              sub={kpis.olv_allocation_pct != null ? `${kpis.olv_allocation_pct.toFixed(1)}% allocation` : ""}
              icon={Youtube}
              accent="cyan"
            />
            <KpiCard
              testId="kpi-universe"
              label="Universe Size"
              value={fmtNumber(kpis.universe_size)}
              sub={camp?.target_group ? `${camp.target_group} audience` : ""}
              icon={Users}
              accent="teal"
            />
          </div>

          {/* KPI Row 2 - Results */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {kpis.tv_reach != null ? (
              <KpiCard testId={TID.kpiTvReach} label="TV Reach 1+" value={fmtPercent(kpis.tv_reach)}
                sub={kpis.tv_grp ? `${fmtNumber(kpis.tv_grp)} GRP` : ""} icon={Target} accent="deep" />
            ) : <EmptyKpi label="TV Reach 1+" hint="Run Optimizer to calculate" />}

            {kpis.olv_reach != null ? (
              <KpiCard testId={TID.kpiOlvReach} label="OLV Reach 1+" value={fmtPercent(kpis.olv_reach)}
                sub={kpis.olv_impressions ? `${fmtNumber(kpis.olv_impressions)} impressions` : ""} icon={Youtube} accent="cyan" />
            ) : <EmptyKpi label="OLV Reach 1+" hint="Run Optimizer to calculate" />}

            {kpis.combined_reach != null ? (
              <KpiCard
                testId={TID.kpiCombinedReach}
                label="Combined Reach"
                value={fmtPercent(kpis.combined_reach)}
                sub={`DDF ${kpis.ddf?.toFixed(3) || "1.061"} applied`}
                icon={Layers} accent="teal"
                trend={kpis.tv_reach != null ? (kpis.combined_reach - kpis.tv_reach) : null}
              />
            ) : <EmptyKpi label="Combined Reach" hint="Run Optimizer to calculate" />}

            {kpis.saving_vs_tv_only != null ? (
              <KpiCard
                testId={TID.kpiSaving}
                label="Saving vs TV Only"
                value={`${camp?.currency || "IDR"} ${fmtCurrency(kpis.saving_vs_tv_only)}`}
                sub={kpis.effective_cprp ? `Eff. CPRP ${fmtCurrency(kpis.effective_cprp)}` : ""}
                icon={PiggyBank} accent="blue"
              />
            ) : <EmptyKpi label="Saving vs TV Only" hint="Run Optimizer to calculate" />}
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Budget split */}
            <div className="card-harmony p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display text-base font-semibold text-slate-900">Budget Split</h3>
                <span className="text-xs text-slate-500">TV vs OLV</span>
              </div>
              {totalBudget > 0 ? (
                <>
                  <div className="h-64 min-h-[256px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={budgetSplit}
                          innerRadius={56}
                          outerRadius={88}
                          paddingAngle={2}
                          dataKey="value"
                          label={(e) => `${e.name} ${((e.value / totalBudget) * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {budgetSplit.map((e) => <Cell key={e.name} fill={e.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${camp?.currency || "IDR"} ${fmtCurrency(v)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                    {budgetSplit.map((b) => (
                      <div key={b.name} className="flex items-center gap-2 text-sm">
                        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: b.color }} />
                        <span className="text-slate-700 font-medium">{b.name}</span>
                        <span className="ml-auto text-slate-500">{camp?.currency || "IDR"} {fmtCurrency(b.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-400 py-16 text-center">No budget data</div>
              )}
            </div>

            {/* Reach contribution */}
            <div className="card-harmony p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display text-base font-semibold text-slate-900">Reach Contribution</h3>
                <span className="text-xs text-slate-500">% reach</span>
              </div>
              {reachContribution.length ? (
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart data={reachContribution} margin={{ top: 15, right: 10, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="name" fontSize={11} stroke="#64748B" />
                      <YAxis fontSize={11} stroke="#64748B" />
                      <Tooltip formatter={(v) => `${v.toFixed(2)}%`} />
                      <Bar dataKey="reach" radius={[8,8,0,0]}>
                        {reachContribution.map((d, i) => <Cell key={d.name} fill={CHART_COLORS[i]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-sm text-slate-400 py-16 text-center">
                  Run Optimizer to see reach contribution
                </div>
              )}
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                Combined Reach = TV + OLV − (DDF × TV × OLV)
              </div>
            </div>

            {/* Efficiency */}
            <div className="card-harmony p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-display text-base font-semibold text-slate-900">Efficiency</h3>
                <Calculator className="h-4 w-4 text-slate-400" />
              </div>
              <div className="space-y-3 mt-3">
                <div className="p-3 rounded-lg bg-[#0A3D91]/5">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Effective CPRP</div>
                  <div className="font-display text-xl font-bold text-[#0A3D91] mt-1" data-testid={TID.kpiEffCprp}>
                    {kpis.effective_cprp != null ? `${camp?.currency || "IDR"} ${fmtCurrency(kpis.effective_cprp)}` : "—"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">Total budget ÷ (combined reach × 100)</div>
                </div>
                <div className="p-3 rounded-lg bg-[#1CC7B3]/8">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">DDF Factor</div>
                  <div className="font-display text-xl font-bold text-[#0B8F80] mt-1" data-testid={TID.kpiDdf}>
                    {kpis.ddf ? kpis.ddf.toFixed(3) : "—"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    TV × OLV de-duplication{dashboard?.ddf_used_default ? " (default)" : ""}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[#18B7D8]/8">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">TV-only Equivalent</div>
                  <div className="font-display text-xl font-bold text-[#0E73C9] mt-1">
                    {kpis.tv_only_grp_equiv != null ? `${fmtNumber(kpis.tv_only_grp_equiv)} GRP` : "—"}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {kpis.combined_reach != null ? `To match ${fmtPercent(kpis.combined_reach)} reach` : "Run Optimizer to calculate"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Curve (mirrors Curve Library visualization) */}
          <div className="card-harmony p-5" data-testid="dashboard-selected-curve">
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-display text-base font-semibold text-slate-900">Selected Curve</h3>
                <p className="text-xs text-slate-500">
                  {dashboard?.curve_set
                    ? `${dashboard.curve_set.curve_name || "Curve set"}${dashboard.curve_set.target_audience ? ` · ${dashboard.curve_set.target_audience}` : ""}${dashboard.curve_set.universe_size ? ` · Universe ${fmtNumber(dashboard.curve_set.universe_size)}` : ""}`
                    : "No curve set selected for this campaign."}
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Use this to compare curve options against the same budget and parameters.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={openReplaceCurve}
                  className="btn-secondary text-xs"
                  data-testid="dashboard-replace-curve-btn"
                >
                  <Shuffle className="h-3.5 w-3.5" /> Replace Curve Set
                </button>
                {dashboard?.curve_set && (
                  <button
                    onClick={() => nav("/curve-library")}
                    className="text-xs font-medium text-[#0E73C9] hover:underline"
                  >
                    Open in Curve Library →
                  </button>
                )}
              </div>
            </div>
            {!dashboard?.curve_set ? (
              <div className="py-12 text-center" data-testid="dashboard-selected-curve-empty">
                <FileSpreadsheet className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                <div className="text-sm font-medium text-slate-700">No curve set selected for this campaign.</div>
                <div className="text-xs text-slate-500 mt-1">
                  Select a curve set in Campaigns or Optimizer to display the curve here.
                </div>
              </div>
            ) : curveLoading || !curveDetail ? (
              <div className="py-16 text-center">
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" />
              </div>
            ) : (
              <CurveChart detail={curveDetail} showTable={false} chartHeight={240} />
            )}
          </div>

          {/* Budget sensitivity — pinned saved analysis for this campaign */}
          {(() => {
            const saved = dashboard?.saved_sensitivity;
            const savedPoints = saved?.result_snapshot?.budget_vs_reach || saved?.result_snapshot?.points || [];
            const savedAt = saved?.created_at ? new Date(saved.created_at) : null;
            return (
              <div className="card-harmony p-5" data-testid="dashboard-saved-sensitivity">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <div>
                    <h3 className="font-display text-base font-semibold text-slate-900">Budget Sensitivity</h3>
                    <p className="text-xs text-slate-500">
                      {saved
                        ? `Saved analysis · ${saved.curve_set_name || "curve set"}${savedAt ? ` · ${savedAt.toLocaleDateString()}` : ""}`
                        : "Reach vs total budget — saved analysis for this campaign"}
                    </p>
                  </div>
                  <button onClick={() => nav("/budget-sensitivity")} className="text-xs font-medium text-[#0E73C9] hover:underline" data-testid="dashboard-bs-open">
                    {saved ? "Open Budget Sensitivity →" : "Run an analysis →"}
                  </button>
                </div>
                {savedPoints.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer>
                      <AreaChart data={savedPoints} margin={{ top: 10, right: 20, left: -10 }}>
                        <defs>
                          <linearGradient id="grad-combined" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1CC7B3" stopOpacity={0.5} />
                            <stop offset="95%" stopColor="#1CC7B3" stopOpacity={0.05} />
                          </linearGradient>
                          <linearGradient id="grad-tvonly" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0A3D91" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#0A3D91" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis dataKey="budget_total" fontSize={11} stroke="#64748B" tickFormatter={(v) => `${(v/1e9).toFixed(1)}B`} />
                        <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `${v.toFixed(0)}%`} />
                        <Tooltip formatter={(v, name) => [`${Number(v).toFixed(2)}%`, name]} labelFormatter={(v) => `${camp?.currency || "IDR"} ${fmtCurrency(v)}`} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="reach_vnp" name="Combined Reach" stroke="#1CC7B3" fill="url(#grad-combined)" strokeWidth={2.5} />
                        <Area type="monotone" dataKey="reach_tv_only" name="TV Only" stroke="#0A3D91" fill="url(#grad-tvonly)" strokeWidth={2.5} strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="reach_tv" name="TV" stroke="#0E73C9" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="reach_olv" name="OLV" stroke="#18B7D8" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="py-16 text-center text-sm text-slate-400" data-testid="dashboard-bs-empty">
                    No budget sensitivity data available. Run an analysis to populate.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Scenario comparison */}
          {dashboard?.campaign_scenarios?.length > 0 && (
            <div className="card-harmony p-5">
              <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
                Scenario Comparison ({dashboard.campaign_scenarios.length})
              </h3>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={dashboard.campaign_scenarios.slice().reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="scenario_name" fontSize={10} stroke="#64748B" />
                    <YAxis fontSize={11} stroke="#64748B" tickFormatter={(v) => `${v.toFixed(0)}%`} />
                    <Tooltip formatter={(v) => `${v?.toFixed(2)}%`} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="tv_reach" name="TV Reach" fill="#0A3D91" radius={[4,4,0,0]} />
                    <Bar dataKey="olv_reach" name="OLV Reach" fill="#18B7D8" radius={[4,4,0,0]} />
                    <Bar dataKey="combined_reach" name="Combined" fill="#1CC7B3" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Bottom tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="card-harmony p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-base font-semibold text-slate-900">Latest Campaigns</h3>
                <button onClick={() => nav("/campaigns")} className="text-xs font-medium text-[#0E73C9] hover:underline">View all →</button>
              </div>
              <div className="space-y-2">
                {(dashboard?.latest_campaigns || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => { setActiveCampaignId(c.id); toast.success(`Switched to ${c.campaign_name}`); }}>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{c.campaign_name}</div>
                      <div className="text-xs text-slate-500">{c.client} · {c.target_group}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-slate-900">{c.currency || "IDR"} {fmtCurrency(c.total_budget)}</div>
                      <div className="text-xs text-slate-500">{c.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-harmony p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-base font-semibold text-slate-900">Uploaded Curve Sets</h3>
                <button onClick={() => nav("/curve-library")} className="text-xs font-medium text-[#0E73C9] hover:underline">View all →</button>
              </div>
              <div className="space-y-2">
                {(dashboard?.latest_curves || []).map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{c.curve_name}</div>
                      <div className="text-xs text-slate-500">{c.target_audience || "—"} · Universe {fmtNumber(c.universe_size)}</div>
                    </div>
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Replace Curve Set confirmation dialog */}
      <Dialog open={replaceCurveOpen} onOpenChange={(o) => { if (!o) setReplaceCurveOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="replace-curve-dialog">
          <DialogHeader>
            <DialogTitle>Replace Curve Set</DialogTitle>
            <DialogDescription>
              Replace the curve set for this active campaign? Dashboard calculations and curve visualization will refresh using the selected curve set. Historical saved scenarios will remain unchanged.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Curve Set</label>
            <Select value={pendingCurveId} onValueChange={setPendingCurveId}>
              <SelectTrigger className="mt-2" data-testid="replace-curve-select">
                <SelectValue placeholder="Select a curve set" />
              </SelectTrigger>
              <SelectContent>
                {allCurves.map((cs) => (
                  <SelectItem key={cs.id} value={cs.id}>
                    {cs.curve_name}{cs.target_audience ? ` · ${cs.target_audience}` : ""}{cs.universe_size ? ` · ${fmtNumber(cs.universe_size)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 mt-2">
              Campaign budget, vendor parameters and the parameter snapshot are kept as-is — only the curve reference changes.
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setReplaceCurveOpen(false)} className="btn-secondary" data-testid="replace-curve-cancel">Cancel</button>
            <button
              onClick={confirmReplaceCurve}
              disabled={replaceBusy || !pendingCurveId || pendingCurveId === dashboard?.curve_set?.id}
              className="btn-primary"
              data-testid="replace-curve-confirm"
            >
              {replaceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shuffle className="h-4 w-4" />} Replace Curve Set
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
